import { forEach } from "lodash"
import { buildContributorKey } from "helpers/contributors"
import { camelCaseToSpace } from "helpers/string"
import { generateClassificationQueryForContributorDescriptors, classificationsMap } from "helpers/classification"

interface ClassificationValue {
  name: string
  id: string
}

interface ClassificationTier {
  id: string
  level: number
  name: string
}

interface ClassificationTierGroup {
  id: string
  versionId: string
  levels: number
  tiers?: ClassificationTier[]
}

interface Classification {
  id: string
  versions: { id: string, asOf: string, tiers: ClassificationTier[] }[]
}

interface SecurityBase {
  id: string
  sedol: string | null
  figi: string | null
  bloombergTicker: string | null
  assetClass: string | null
  assetSubclass: string | null
  ticker: string | null
  mic: string | null
  description: string | null
  averageDailyVolume: number | null
  marketCapitalization: number | null
  country: string | null
  currency: string | null
  modelProviderId: string | null
}

type Security = SecurityBase & Record<string, string | number | null | ClassificationValue>

type DescriptorData = Record<string, string | number | null>

interface ContributorValues {
  identifier?: string
  mic?: string
  internalId?: string | null
  modelProviderId?: string | null
  assetClass?: string | null
  assetSubclass?: string | null
  bloombergTicker?: string | null
  economicExposure?: number
  positionSetEconomicExposure?: number
  baseEconomicExposure?: number
  country?: string | null
  currency?: string | null
  description?: string | null
  notHeld?: boolean
  sector?: string | null
  industry?: string | null
  sedol?: string | null
  figi?: string | null
  ticker?: string | null
  averageDailyVolume?: number | null
  marketCapitalization?: number | null
  descriptors?: DescriptorData
  [key: string]: string | number | boolean | null | undefined | DescriptorData
}

interface Contributor {
  id?: string
  label?: string
  notHeld?: boolean
  isNoLongerHeld?: boolean
  isUnmodeled?: boolean
  isFactor?: boolean
  pinnedRowOrder?: number
  tooltipValue?: string
  category?: string
  values: Record<string, ContributorValues>
}

interface Child {
  id?: string
  label?: string
  notHeld?: boolean
  isNoLongerHeld?: boolean
  isUnmodeled?: boolean
  isFactor?: boolean
  pinnedRowOrder?: number
  tooltipValue?: string
  children?: Record<string, Contributor>
  values: Record<string, ContributorValues>
}

export function buildDescriptorsMap(securities: Security[]): Record<string, DescriptorData> {
  return securities.reduce((acc, security) => {
    const key = buildContributorKey({
      ...security,
      identifier: security.id,
    })
    acc[key] = {
      ...flattenClassificationValues(security),
      assetClass: camelCaseToSpace(security.assetClass),
      assetSubclass: camelCaseToSpace(security.assetSubclass),
    }
    return acc
  }, {} as Record<string, DescriptorData>)
}

export function mergeDescriptorsIntoChildren(
  children: Record<string, Child>,
  descriptorsMap: Record<string, DescriptorData>,
  isGrouped: boolean,
  descriptorsKey: string,
): void {
  forEach(children, (child, key) => {
    if (isGrouped && child.children) {
      forEach(child.children, (contributor, childKey) => {
        const descriptorData = descriptorsMap[childKey]
        if (descriptorData) {
          contributor.values[descriptorsKey] = descriptorData
        }
      })
    } else {
      const descriptorData = descriptorsMap[key]
      if (descriptorData) {
        child.values[descriptorsKey] = descriptorData
      }
    }
  })
}

export const BATCH_SIZE = 200

export function getDescriptorsQueryFragment(classifications: Classification[], to: string, count: number, iteration: number, maxBatches: number): string {
  if (!count) {
    return ""
  }

  const fragments: string[] = []
  let skip = iteration ? iteration * BATCH_SIZE * maxBatches : 0
  let batchIndex = 0

  while (skip < count && batchIndex < maxBatches) {
    fragments.push(`batch${batchIndex}: securitySearch (filter: $filter, on: $date, skip: ${skip}, sort: $sort, currency: $currency) {
      securities {
        id
        sedol
        cusip
        isin
        figi
        bloombergTicker
        assetClass
        assetSubclass
        ticker
        mic
        description
        averageDailyVolume
        marketCapitalization
        country
        currency
        modelProviderId
        ${generateAllClassificationQueryParts(classificationsMap(classifications, to))}
      }
    }`)
    skip += BATCH_SIZE
    batchIndex++
  }

  return fragments.join("\n")
}

function generateAllClassificationQueryParts(classificationTiers: ClassificationTierGroup[]): string {
  return classificationTiers
    .map((classification) => {
      const tiers = classification.tiers?.map((tier) => tier.id)
      return generateClassificationQueryForContributorDescriptors(classification.id, classificationTiers, tiers)
    })
    .join("\n")
}

function isClassificationValue(v: string | number | null | ClassificationValue): v is ClassificationValue {
  return !!v
    && typeof v === "object"
    && "name" in v
    && "id" in v
    && typeof v.name === "string"
    && typeof v.id === "string"
}

function flattenClassificationValues(security: Security): DescriptorData {
  const result: DescriptorData = {}

  for (const [key, value] of Object.entries(security)) {
    if (isClassificationValue(value)) {
      result[key] = value.name
    } else if (typeof value === "string" || typeof value === "number" || value === null) {
      result[key] = value
    }
  }

  return result
}
